"use client";

import { useEffect, useCallback, useRef } from "react";
import { FormState, TreeConfig } from "../types";
import {
  extractChildItemsFromResponse,
  findItemsWithChildren,
  buildParentDropdownOptions,
  extractParentComponentFields,
  buildDefaultFieldValues,
} from "../utils";

interface UseTreeDataProps {
  selectedSection: { id: string; componentId?: string; data?: any };
  formState: FormState;
  setFormState: React.Dispatch<React.SetStateAction<FormState>>;
  treeConfig?: TreeConfig;
  componentFieldsConfig: any[];
  parentComponentFieldsConfig: any[];
  fetchMethod?: (id: number) => Promise<any>;
}

export const useTreeData = ({
  selectedSection,
  formState,
  setFormState,
  treeConfig,
  componentFieldsConfig,
  parentComponentFieldsConfig,
  fetchMethod,
}: UseTreeDataProps) => {
  const lastFetchedParentComponentId = useRef<string | number | null>(null);

  // Fetch tree items
  const fetchTreeItems = useCallback(async () => {
    const parentComponentId = selectedSection?.componentId;

    if (!fetchMethod || !parentComponentId) {
      setFormState((prev) => ({ ...prev, treeItems: [] }));
      return;
    }

    try {
      const response = await fetchMethod(Number(parentComponentId));
      const childItemsFieldName = treeConfig?.childItemsFieldName || "items";
      const { items, parentComponentData } = extractChildItemsFromResponse(
        response,
        childItemsFieldName
      );

      setFormState((prev) => ({
        ...prev,
        treeItems: items,
        expandedIds: treeConfig ? findItemsWithChildren(items, treeConfig) : new Set(),
        parentDropdownOptions: treeConfig?.parentFieldName
          ? buildParentDropdownOptions(items, treeConfig, prev.editingItemId)
          : [],
        ...(parentComponentData && {
          parentComponentFields: extractParentComponentFields(
            parentComponentData,
            parentComponentFieldsConfig
          ),
          parentComponentId: parentComponentData.id || prev.parentComponentId,
        }),
      }));
    } catch (error) {
      console.error("Failed to fetch tree items:", error);
    }
  }, [selectedSection?.componentId, fetchMethod, treeConfig, parentComponentFieldsConfig, setFormState]);

  // Initialize tree data from section or fetch
  useEffect(() => {
    const parentComponentId = selectedSection?.componentId;
    const childItemsFieldName = treeConfig?.childItemsFieldName || "items";

    // Define initialization strategies
    const initStrategies = {
      useExistingData: {
        canUse: () => {
          const items = selectedSection?.data?.[childItemsFieldName] || [];
          return selectedSection?.data && parentComponentId && Array.isArray(items) && items.length > 0;
        },
        execute: () => {
          const items = selectedSection.data[childItemsFieldName] || [];
          setFormState((prev) => ({
            ...prev,
            treeItems: items,
            expandedIds: treeConfig ? findItemsWithChildren(items, treeConfig) : new Set(),
            parentDropdownOptions: treeConfig?.parentFieldName
              ? buildParentDropdownOptions(items, treeConfig, prev.editingItemId)
              : [],
          }));
          lastFetchedParentComponentId.current = parentComponentId ?? null;
        },
      },
      clearData: {
        canUse: () => !parentComponentId,
        execute: () => {
          lastFetchedParentComponentId.current = null;
          setFormState((prev) => ({ ...prev, treeItems: [] }));
        },
      },
      fetchData: {
        canUse: () =>
          parentComponentId &&
          lastFetchedParentComponentId.current !== parentComponentId &&
          fetchMethod,
        execute: () => {
          lastFetchedParentComponentId.current = parentComponentId ?? null;
          fetchTreeItems();
        },
      },
    };

    // Execute first matching strategy
    const strategyOrder = ["useExistingData", "clearData", "fetchData"] as const;
    for (const key of strategyOrder) {
      const strategy = initStrategies[key];
      if (strategy.canUse()) {
        strategy.execute();
        return;
      }
    }
  }, [
    selectedSection?.componentId,
    selectedSection?.id,
    selectedSection?.data,
    treeConfig,
    fetchMethod,
    fetchTreeItems,
    setFormState,
  ]);

  // Initialize form fields with defaults
  useEffect(() => {
    if (Object.keys(formState.componentFields).length === 0 && componentFieldsConfig.length > 0) {
      setFormState((prev) => ({
        ...prev,
        componentFields: buildDefaultFieldValues(componentFieldsConfig),
      }));
    }
  }, [componentFieldsConfig, formState.componentFields, setFormState]);

  // Update parent dropdown options when editing item changes
  useEffect(() => {
    if (treeConfig?.parentFieldName && formState.treeItems.length > 0) {
      const newOptions = buildParentDropdownOptions(
        formState.treeItems,
        treeConfig,
        formState.editingItemId
      );

      setFormState((prev) => {
        const hasChanged = JSON.stringify(prev.parentDropdownOptions) !== JSON.stringify(newOptions);
        return hasChanged ? { ...prev, parentDropdownOptions: newOptions } : prev;
      });
    }
  }, [formState.editingItemId, formState.treeItems, treeConfig, setFormState]);

  return { fetchTreeItems };
};

